/**
 * Saved Prompts Modal Component
 * 
 * Modal for managing and using saved prompts.
 */

import { AIAssistantService } from '../../services/domain/ai-assistant-service.js';
import { Icons } from './utils/icons.js';
import { Logger } from '../../services/utils/logger.js';
import { t } from '../../i18n.js';

/**
 * Create the saved prompts modal
 * @param {Object} options
 * @param {Object} options.state - AIAssistantState instance
 * @param {Function} options.onSelectPrompt - Callback when prompt is selected
 * @param {Function} options.onClose - Callback to close modal
 * @returns {HTMLElement}
 */
export function createSavedPromptsModal({ state, onSelectPrompt, onClose }) {
  const overlay = document.createElement('div');
  overlay.className = 'ai-modal-overlay';
  overlay.onclick = (e) => {
    if (e.target === overlay) handleClose();
  };
  
  // Close with animation
  function handleClose() {
    overlay.classList.remove('open');
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  }
  
  const modal = document.createElement('div');
  modal.className = 'ai-modal saved-prompts-modal';
  
  // Header
  const header = document.createElement('div');
  header.className = 'ai-modal-header';
  
  const title = document.createElement('h2');
  title.textContent = t('ai_saved_prompts_title');
  
  const closeBtn = document.createElement('button');
  closeBtn.className = 'ai-modal-close';
  closeBtn.innerHTML = Icons.close();
  closeBtn.onclick = handleClose;
  
  header.appendChild(title);
  header.appendChild(closeBtn);
  
  // Search and filter bar
  const toolbar = document.createElement('div');
  toolbar.className = 'ai-modal-toolbar';
  
  const searchInput = document.createElement('input');
  searchInput.type = 'text';
  searchInput.className = 'ai-search-input';
  searchInput.placeholder = t('ai_search_prompts_placeholder');
  
  const addBtn = document.createElement('button');
  addBtn.className = 'ai-add-prompt-btn';
  addBtn.innerHTML = Icons.plus();
  addBtn.title = t('ai_new_prompt_title');
  addBtn.onclick = () => showPromptForm(null);
  
  toolbar.appendChild(searchInput);
  toolbar.appendChild(addBtn);
  
  // Category filter bar (between toolbar and content)
  const filterBar = document.createElement('div');
  filterBar.className = 'ai-filter-bar';
  filterBar.id = 'ai-category-filter';
  filterBar.style.display = 'none'; // Hidden by default until categories exist
  
  // Content
  const content = document.createElement('div');
  content.className = 'ai-modal-content';
  
  const promptsList = document.createElement('div');
  promptsList.className = 'ai-prompts-list';
  promptsList.id = 'ai-prompts-list';
  
  content.appendChild(promptsList);
  
  // Prompt form (hidden by default)
  const formContainer = document.createElement('div');
  formContainer.className = 'ai-prompt-form-container';
  formContainer.id = 'ai-prompt-form';
  formContainer.style.display = 'none';
  
  modal.appendChild(header);
  modal.appendChild(toolbar);
  modal.appendChild(filterBar);
  modal.appendChild(content);
  modal.appendChild(formContainer);
  overlay.appendChild(modal);
  
  // State
  let currentEditingPrompt = null;
  let searchTerm = '';
  let selectedCategory = null; // null means "All"
  
  // Search handler
  searchInput.addEventListener('input', (e) => {
    searchTerm = e.target.value.toLowerCase();
    renderPrompts();
  });
  
  // Get unique categories from prompts
  function getCategories() {
    const prompts = state.savedPrompts || [];
    const categories = new Set();
    prompts.forEach(p => {
      if (p.category) categories.add(p.category);
    });
    return Array.from(categories).sort();
  }
  
  // Render category filters
  function renderCategoryFilters() {
    const categories = getCategories();
    
    if (categories.length === 0) {
      filterBar.style.display = 'none';
      return;
    }
    
    filterBar.style.display = 'flex';
    filterBar.innerHTML = '';
    
    // "All" button
    const allBtn = document.createElement('button');
    allBtn.className = `ai-filter-btn${selectedCategory === null ? ' active' : ''}`;
    allBtn.textContent = t('ai_filter_all');
    allBtn.onclick = () => {
      selectedCategory = null;
      renderCategoryFilters();
      renderPrompts();
    };
    filterBar.appendChild(allBtn);
    
    // Category buttons
    categories.forEach(category => {
      const btn = document.createElement('button');
      btn.className = `ai-filter-btn${selectedCategory === category ? ' active' : ''}`;
      btn.textContent = category;
      btn.onclick = () => {
        selectedCategory = category;
        renderCategoryFilters();
        renderPrompts();
      };
      filterBar.appendChild(btn);
    });
  }
  
  // Render prompts
  function renderPrompts() {
    renderCategoryFilters();
    
    const prompts = state.savedPrompts || [];
    const filtered = prompts.filter(p => {
      // Category filter
      if (selectedCategory !== null && p.category !== selectedCategory) {
        return false;
      }
      
      // Search filter
      if (searchTerm) {
        return p.title.toLowerCase().includes(searchTerm) ||
               p.prompt.toLowerCase().includes(searchTerm) ||
               (p.category && p.category.toLowerCase().includes(searchTerm));
      }
      
      return true;
    });
    
    promptsList.innerHTML = '';
    
    if (state.isLoading) {
      promptsList.innerHTML = `<div class="ai-loading">${t('ai_loading_prompts')}</div>`;
      return;
    }
    
    if (filtered.length === 0) {
      promptsList.innerHTML = `
        <div class="ai-empty-prompts">
          <div class="ai-empty-icon">${Icons.bookmarkFilled()}</div>
          <p>${searchTerm ? t('ai_no_matching_prompts') : t('ai_no_saved_prompts')}</p>
          <p class="ai-empty-hint">${t('ai_create_first_prompt')}</p>
        </div>
      `;
      return;
    }
    
    filtered.forEach(prompt => {
      promptsList.appendChild(createPromptItem(prompt));
    });
  }
  
  function createPromptItem(prompt) {
    const item = document.createElement('div');
    item.className = 'ai-prompt-item';
    item.dataset.promptId = prompt._id;
    
    // Main content (clickable to use)
    const mainContent = document.createElement('div');
    mainContent.className = 'ai-prompt-main';
    mainContent.onclick = () => {
      if (onSelectPrompt) onSelectPrompt(prompt.prompt);
      handleClose();
    };
    
    const titleRow = document.createElement('div');
    titleRow.className = 'ai-prompt-title-row';
    
    const titleEl = document.createElement('span');
    titleEl.className = 'ai-prompt-title';
    titleEl.textContent = prompt.title;
    
    if (prompt.isFavorite) {
      const star = document.createElement('span');
      star.className = 'ai-prompt-star';
      star.innerHTML = Icons.star();
      titleRow.appendChild(star);
    }

    if (prompt.isShared) {
      const shared = document.createElement('span');
      shared.className = 'ai-prompt-shared';
      shared.textContent = t('ai_prompt_shared');
      titleRow.appendChild(shared);
    }
    
    titleRow.insertBefore(titleEl, titleRow.firstChild);
    
    const promptText = document.createElement('div');
    promptText.className = 'ai-prompt-text';
    promptText.textContent = truncateText(prompt.prompt, 100);
    
    const meta = document.createElement('div');
    meta.className = 'ai-prompt-meta';
    
    if (prompt.category) {
      const category = document.createElement('span');
      category.className = 'ai-prompt-category';
      category.textContent = prompt.category;
      meta.appendChild(category);
    }
    
    const usageCount = document.createElement('span');
    usageCount.className = 'ai-prompt-usage';
    usageCount.textContent = t('ai_used_times', { n: String(prompt.usageCount || 0) });
    meta.appendChild(usageCount);
    
    mainContent.appendChild(titleRow);
    mainContent.appendChild(promptText);
    mainContent.appendChild(meta);
    
    // Actions
    const actions = document.createElement('div');
    actions.className = 'ai-prompt-actions';
    
    const editBtn = document.createElement('button');
    editBtn.className = 'ai-prompt-action-btn';
    editBtn.title = t('ai_edit_title');
    editBtn.innerHTML = Icons.edit();
    editBtn.onclick = (e) => {
      e.stopPropagation();
      showPromptForm(prompt);
    };
    
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'ai-prompt-action-btn delete';
    deleteBtn.title = t('ai_delete_title');
    deleteBtn.innerHTML = Icons.delete();
    deleteBtn.onclick = (e) => {
      e.stopPropagation();
      handleDeletePrompt(prompt._id);
    };
    
    actions.appendChild(editBtn);
    actions.appendChild(deleteBtn);
    
    item.appendChild(mainContent);
    item.appendChild(actions);
    
    return item;
  }
  
  function showPromptForm(prompt) {
    currentEditingPrompt = prompt;
    content.style.display = 'none';
    toolbar.style.display = 'none';
    filterBar.style.display = 'none';
    formContainer.style.display = 'block';
    
    formContainer.innerHTML = '';
    formContainer.appendChild(createPromptForm(prompt, {
      onSave: handleSavePrompt,
      onCancel: hidePromptForm
    }));
  }
  
  function hidePromptForm() {
    currentEditingPrompt = null;
    formContainer.style.display = 'none';
    content.style.display = 'block';
    toolbar.style.display = 'flex';
    renderCategoryFilters(); // Re-render filters when returning
  }
  
  async function handleSavePrompt(data) {
    try {
      if (currentEditingPrompt) {
        const updatedPrompt = await AIAssistantService.updateSavedPrompt(currentEditingPrompt._id, data);
        state.updateSavedPrompt(currentEditingPrompt._id, updatedPrompt);
      } else {
        const newPrompt = await AIAssistantService.createSavedPrompt(data);
        state.addSavedPrompt(newPrompt);
      }
      hidePromptForm();
      renderPrompts();
    } catch (error) {
      Logger.error('Failed to save prompt', error);
      alert(t('ai_save_prompt_failed'));
    }
  }
  
  async function handleDeletePrompt(promptId) {
    if (!confirm(t('ai_delete_prompt_confirm'))) return;
    
    try {
      await AIAssistantService.deleteSavedPrompt(promptId);
      state.removeSavedPrompt(promptId);
      renderPrompts();
    } catch (error) {
      Logger.error('Failed to delete prompt', error);
      alert(t('ai_delete_prompt_failed'));
    }
  }
  
  // Initial render
  renderPrompts();
  
  // ESC key to close
  document.addEventListener('keydown', function escHandler(e) {
    if (e.key === 'Escape') {
      document.removeEventListener('keydown', escHandler);
      handleClose();
    }
  });
  
  return overlay;
}

/**
 * Create the prompt form
 */
function createPromptForm(prompt, { onSave, onCancel }) {
  const form = document.createElement('div');
  form.className = 'ai-prompt-form';
  
  const title = document.createElement('h3');
  title.textContent = prompt ? t('ai_edit_prompt_title') : t('ai_new_prompt_title');
  
  // Title input
  const titleGroup = createFormGroup(t('ai_form_title_label'), 'text', 'prompt-title', prompt?.title || '');
  
  // Prompt textarea
  const promptGroup = document.createElement('div');
  promptGroup.className = 'ai-form-group';
  
  const promptLabel = document.createElement('label');
  promptLabel.textContent = t('ai_form_prompt_label');
  
  const promptTextarea = document.createElement('textarea');
  promptTextarea.className = 'ai-form-textarea';
  promptTextarea.id = 'prompt-content';
  promptTextarea.rows = 4;
  promptTextarea.value = prompt?.prompt || '';
  promptTextarea.placeholder = t('ai_form_prompt_placeholder');
  
  promptGroup.appendChild(promptLabel);
  promptGroup.appendChild(promptTextarea);
  
  // Category input
  const categoryGroup = createFormGroup(t('ai_form_category_label'), 'text', 'prompt-category', prompt?.category || '', t('ai_form_category_placeholder'));
  
  // Checkbox row
  const checkboxRow = document.createElement('div');
  checkboxRow.className = 'ai-checkbox-row';
  
  const favoriteCheck = createCheckbox(t('ai_form_favorite_label'), 'prompt-favorite', prompt?.isFavorite || false);
  const sharedCheck = createCheckbox(t('ai_form_shared_label'), 'prompt-shared', prompt?.isShared || false);

  checkboxRow.appendChild(favoriteCheck);
  checkboxRow.appendChild(sharedCheck);
  
  // Buttons
  const buttons = document.createElement('div');
  buttons.className = 'ai-form-buttons';
  
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'ai-form-btn secondary';
  cancelBtn.type = 'button';
  cancelBtn.textContent = t('ai_cancel');
  cancelBtn.onclick = onCancel;
  
  const saveBtn = document.createElement('button');
  saveBtn.className = 'ai-form-btn primary';
  saveBtn.type = 'button';
  saveBtn.textContent = prompt ? t('ai_save_changes') : t('ai_create_prompt');
  saveBtn.onclick = () => {
    const titleInput = form.querySelector('#prompt-title');
    const contentInput = form.querySelector('#prompt-content');
    const categoryInput = form.querySelector('#prompt-category');
    const favoriteInput = form.querySelector('#prompt-favorite');
    const sharedInput = form.querySelector('#prompt-shared');
    
    if (!titleInput.value.trim()) {
      alert(t('ai_enter_title'));
      return;
    }
    
    if (!contentInput.value.trim()) {
      alert(t('ai_enter_prompt'));
      return;
    }
    
    onSave({
      title: titleInput.value.trim(),
      prompt: contentInput.value.trim(),
      category: categoryInput.value.trim() || undefined,
      isFavorite: favoriteInput.checked,
      isShared: sharedInput.checked
    });
  };
  
  buttons.appendChild(cancelBtn);
  buttons.appendChild(saveBtn);
  
  form.appendChild(title);
  form.appendChild(titleGroup);
  form.appendChild(promptGroup);
  form.appendChild(categoryGroup);
  form.appendChild(checkboxRow);
  form.appendChild(buttons);
  
  return form;
}

function createFormGroup(label, type, id, value, placeholder = '') {
  const group = document.createElement('div');
  group.className = 'ai-form-group';
  
  const labelEl = document.createElement('label');
  labelEl.textContent = label;
  labelEl.htmlFor = id;
  
  const input = document.createElement('input');
  input.type = type;
  input.className = 'ai-form-input';
  input.id = id;
  input.value = value;
  input.placeholder = placeholder;
  
  group.appendChild(labelEl);
  group.appendChild(input);
  
  return group;
}

function createCheckbox(label, id, checked) {
  const wrapper = document.createElement('label');
  wrapper.className = 'ai-checkbox-wrapper';
  
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.id = id;
  input.checked = checked;
  
  const span = document.createElement('span');
  span.textContent = label;
  
  wrapper.appendChild(input);
  wrapper.appendChild(span);
  
  return wrapper;
}

/**
 * Truncate text to maximum length
 */
function truncateText(text, maxLength) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}
